import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DISMISSED_KEY = 'hakken_pwa_install_dismissed';

function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // iOS não suporta beforeinstallprompt — mostramos instrução manual
      const timer = setTimeout(() => setVisible(true), 5000);
      return () => clearTimeout(timer);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-24 left-4 right-4 z-[200] rounded-2xl border-2 border-gold/40 bg-[#0e0e1f]/95 p-4 shadow-game-card backdrop-blur-sm"
      >
        <div className="flex items-start gap-3">
          <img src="/icons/logo_192.png" alt="" className="h-12 w-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-body text-sm font-bold text-white">Instala o Hakken City</p>
            {isIOS ? (
              <p className="mt-1 font-body text-xs text-white/60">
                Toca em <span className="font-bold text-white">Partilhar</span> e depois em{' '}
                <span className="font-bold text-white">"Adicionar ao ecrã principal"</span>.
              </p>
            ) : (
              <p className="mt-1 font-body text-xs text-white/60">
                Adiciona ao ecrã inicial para uma experiência a tempo inteiro, mesmo sem internet.
              </p>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 rounded-full p-1 text-white/40 hover:text-white/80"
            aria-label="Fechar"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        {!isIOS && (
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 rounded-xl border border-white/20 py-2 font-body text-sm font-semibold text-white/60 active:scale-95"
            >
              Agora não
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 rounded-xl bg-gold py-2 font-body text-sm font-bold text-pirate-dark active:scale-95"
            >
              Instalar
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
